import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faCopy } from '@fortawesome/free-solid-svg-icons'
import { useSupabaseQuery } from '../../hooks/useSupabase'
import { NUDGGE_API_BASE_URL } from '../../lib/config'
import Modal from '../shared/Modal'

export default function EntriesTab({ selectedAccount }) {
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showWebhookGuide, setShowWebhookGuide] = useState(true)

  // Fetch email entries for the selected account
  const { data: entries, loading, error, refetch } = useSupabaseQuery('EmailEntries', {
    eq: { ...(selectedAccount && { AccountId: selectedAccount.AccountId }) },
    order: { column: 'Created', ascending: false },
    limit: 100
  })

  // Fetch delivery events for selected entry
  const { data: deliveryEvents } = useSupabaseQuery('EmailEntryDeliveryEvents', {
    eq: { ...(selectedEntry && { EmailEntryId: selectedEntry.EmailEntriesId }) },
    order: { column: 'Timestamp', ascending: false }
  }, {
    enabled: !!selectedEntry
  })

  // Fetch templates for reference
  const { data: templates } = useSupabaseQuery('EmailTemplates', {
    eq: { IsDeleted: false, ...(selectedAccount && { AccountId: selectedAccount.AccountId }) }
  })

  const handleViewDetails = (entry) => {
    setSelectedEntry(entry)
    setShowDetailsModal(true)
  }

  const getTemplateName = (templateId) => {
    if (!templateId || !templates) return null
    const template = templates.find(t => t.TemplateIdentifier === templateId)
    return template?.Name || null
  }

  const getStatusBadge = (entry) => {
    // Check if there are any delivery events
    // For now, we'll show "Sent" for all entries
    // This will be enhanced when we fetch events per entry
    return (
      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs rounded">
        Sent
      </span>
    )
  }

  if (!selectedAccount) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Account Selected</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Please select an account from the Accounts tab first
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  const webhookUrl = `${NUDGGE_API_BASE_URL}/functions/v1/Email-Webhook`

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="space-y-6">
      {/* Webhook Configuration Banner */}
      {showWebhookGuide && entries && entries.length > 0 && (!deliveryEvents || deliveryEvents.length === 0) && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                <FontAwesomeIcon icon={faBell} className="mr-2 w-4 h-4" fixedWidth />
                Configure SendGrid Webhook for Delivery Reports
              </h3>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-3">
                To see delivery events (delivered, bounced, opened, clicked), configure SendGrid to send webhooks:
              </p>
              <ol className="text-xs text-yellow-700 dark:text-yellow-300 space-y-2 mb-3 ml-4 list-decimal">
                <li>Go to <strong>SendGrid Dashboard → Settings → Mail Settings → Event Webhook</strong></li>
                <li>Enable Event Notifications and paste this URL:</li>
              </ol>
              <div className="flex items-center space-x-2 mb-3">
                <code className="flex-1 text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-900 dark:text-yellow-100 px-3 py-2 rounded border border-yellow-300 dark:border-yellow-700">
                  {webhookUrl}
                </code>
                <button
                  onClick={() => copyToClipboard(webhookUrl)}
                  className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition"
                  title="Copy to clipboard"
                >
                  <FontAwesomeIcon icon={faCopy} className="mr-1 w-3 h-3" fixedWidth />
                  Copy
                </button>
              </div>
              <ol start="3" className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1 ml-4 list-decimal">
                <li>Select all event types (Processed, Delivered, Bounce, etc.)</li>
                <li>Save configuration</li>
                <li>Send a test email to verify</li>
              </ol>
            </div>
            <button
              onClick={() => setShowWebhookGuide(false)}
              className="flex-shrink-0 text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Email Log</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Email delivery history for: {selectedAccount.Name}
          </p>
        </div>
        <button
          onClick={refetch}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {entries && entries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sent Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {entries.map((entry) => (
                  <tr
                    key={entry.EmailEntriesId}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                  >
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {entry.ToName || entry.ToEmail}
                        </div>
                        {entry.ToName && (
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {entry.ToEmail}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900 dark:text-white">
                          {entry.FromName || entry.FromEmail}
                        </div>
                        {entry.FromName && (
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {entry.FromEmail}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {entry.TemplateIdentifier ? (
                        <div className="text-sm">
                          <div className="text-gray-900 dark:text-white">
                            {getTemplateName(entry.TemplateIdentifier) || 'Unknown'}
                          </div>
                          <code className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 px-1 py-0.5 rounded">
                            {String(entry.TemplateIdentifier).slice(0, 8)}...
                          </code>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">No template</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(entry)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(entry.Created).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleViewDetails(entry)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No emails sent yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Email delivery history will appear here once you start sending emails.
            </p>
          </div>
        )}
      </div>

      {/* Email Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false)
          setSelectedEntry(null)
        }}
        title="Email Details"
        size="lg"
      >
        {selectedEntry && (
          <div className="space-y-6">
            {/* Email Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">To</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {selectedEntry.ToName && <div className="font-medium">{selectedEntry.ToName}</div>}
                  <div className={selectedEntry.ToName ? 'text-gray-600 dark:text-gray-400' : ''}>
                    {selectedEntry.ToEmail}
                  </div>
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">From</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {selectedEntry.FromName && <div className="font-medium">{selectedEntry.FromName}</div>}
                  <div className={selectedEntry.FromName ? 'text-gray-600 dark:text-gray-400' : ''}>
                    {selectedEntry.FromEmail}
                  </div>
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sent Date</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {new Date(selectedEntry.Created).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">External ID</label>
                <p className="text-xs font-mono text-gray-600 dark:text-gray-400 mt-1 break-all">
                  {selectedEntry.ExternalId || 'N/A'}
                </p>
              </div>
            </div>

            {/* Template Info */}
            {selectedEntry.TemplateIdentifier && (
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Template</label>
                <div className="mt-1">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {getTemplateName(selectedEntry.TemplateIdentifier) || 'Unknown Template'}
                  </div>
                  <code className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded mt-1 inline-block">
                    {selectedEntry.TemplateIdentifier}
                  </code>
                </div>
              </div>
            )}

            {/* Delivery Events */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3 block">
                Delivery Events
              </label>
              {deliveryEvents && deliveryEvents.length > 0 ? (
                <div className="space-y-2">
                  {deliveryEvents.map((event) => (
                    <div
                      key={event.Id}
                      className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-shrink-0">
                        {getEventIcon(getEventName(event))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                          {getEventName(event)}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {new Date(event.Timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No delivery events recorded yet
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function getEventName(event) {
  // Handle both string Status and object Status (legacy data)
  if (typeof event.Status === 'string') {
    return event.Status || 'Unknown'
  }
  
  // If Status is an object (legacy), try to extract event name from Reason
  if (event.Reason) {
    try {
      const parsed = typeof event.Reason === 'string' ? JSON.parse(event.Reason) : event.Reason
      return parsed.event || 'Unknown'
    } catch {
      return 'Unknown'
    }
  }
  
  return 'Unknown'
}

function getEventIcon(status) {
  const statusLower = String(status || '').toLowerCase()
  
  if (statusLower.includes('delivered') || statusLower.includes('delivered')) {
    return (
      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
  
  if (statusLower.includes('bounce') || statusLower.includes('failed')) {
    return (
      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
  
  if (statusLower.includes('open') || statusLower.includes('click')) {
    return (
      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    )
  }
  
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}
